import { useState } from 'react'
import { FiShare2 } from 'react-icons/fi'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { AnalyticsFeature } from '@domain/features/analytics/analytics-feature'
import { INSTAGRAM_REEL_FAVOURITE_TYPE } from '@domain/features/user-favourites/user-favourites-feature'
import { CommentsOn } from '@domain/models/activity-comments'
import CommentsSection from '@presentation/components/comments/CommentsSection'
import NewsShareModal from '@presentation/components/news/NewsShareModal'
import FavouriteBookmarkButton from '@presentation/components/ui/FavouriteBookmarkButton'
import InstagramReelEmbed from '@presentation/components/ui/InstagramReelEmbed'
import InstagramReelVideoPlayer from '@presentation/components/ui/InstagramReelVideoPlayer'
import { toInstagramEmbedUrl } from '@presentation/components/ui/instagram-reel-embed-url'
import LoadingSpinner from '@presentation/components/ui/LoadingSpinner'
import { useInstagramReel } from '@presentation/hooks/useInstagramReel'
import { useScrollToTopOnRouteEnter } from '@presentation/hooks/useScrollToTopOnRouteEnter'
import { useUserFavourite } from '@presentation/hooks/useUserFavourite'
import SeoHead from '@presentation/seo/SeoHead'
import StructuredData from '@presentation/seo/StructuredData'
import { buildBreadcrumbList } from '@presentation/seo/StructuredDataBuilders'
import { sanitizeForMeta, toAbsoluteUrl } from '@presentation/seo/seo-constants'

const DR_MICHAEL_OKUN_REELS_ROUTE = '/dr-michael-okun-my-take'

function formatTimestamp(value: Date | null): string {
  if (!value) {
    return 'Date unavailable'
  }

  return value.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

export default function InstagramReelDetailPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const [failedPlaybackKey, setFailedPlaybackKey] = useState<string | null>(null)
  const [isShareModalOpen, setIsShareModalOpen] = useState(false)
  useScrollToTopOnRouteEnter({ scrollOnPop: true })
  const screenPath = `${location.pathname}${location.search || ''}`
  const { reelId } = useParams<{ reelId: string }>()
  const { reel, loading, error, retry } = useInstagramReel(reelId)
  const {
    isFavourited,
    loading: favouriteLoading,
    toggling: favouriteToggling,
    error: favouriteError,
    toggle,
  } = useUserFavourite(reel?.uid, INSTAGRAM_REEL_FAVOURITE_TYPE)

  const openLoginModal = () => {
    const nextParams = new URLSearchParams(location.search)
    nextParams.delete('userProfile')
    nextParams.set('authMode', 'login')

    navigate({
      pathname: location.pathname,
      search: `?${nextParams.toString()}`,
    })
  }

  const navigateBackToReelsList = () => {
    if (location.key !== 'default') {
      navigate(-1)
      return
    }

    navigate(DR_MICHAEL_OKUN_REELS_ROUTE, { replace: true })
  }

  if (loading) {
    return (
      <>
        <SeoHead
          title="Loading Instagram content | Parkinson's Insights"
          canonicalPath={
            reelId ? `${DR_MICHAEL_OKUN_REELS_ROUTE}/${reelId}` : DR_MICHAEL_OKUN_REELS_ROUTE
          }
        />
        <LoadingSpinner className="min-h-[50vh]" />
      </>
    )
  }

  if (!reel) {
    return (
      <>
        <SeoHead
          title="Instagram content not found | Parkinson's Insights"
          canonicalPath={
            reelId ? `${DR_MICHAEL_OKUN_REELS_ROUTE}/${reelId}` : DR_MICHAEL_OKUN_REELS_ROUTE
          }
          noindex
        />
        <div className="flex min-h-[50vh] flex-col items-center justify-center px-4 text-center">
          <p className="text-lg font-semibold text-text-secondary">
            {error ?? 'Instagram content not found'}
          </p>
          <div className="mt-4 flex items-center gap-4">
            <button
              type="button"
              onClick={() => {
                void retry()
              }}
              className="inline-flex rounded-lg bg-blue px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue/90"
            >
              Retry
            </button>
            <Link
              to={DR_MICHAEL_OKUN_REELS_ROUTE}
              className="text-sm font-semibold text-primary hover:text-primary-light"
            >
              &larr; Back to Prof Michael Okun: My Take
            </Link>
          </div>
        </div>
      </>
    )
  }

  const titleLabel = reel.caption || 'Instagram Content'
  const pageDescription = sanitizeForMeta(
    reel.caption || 'Explore this Instagram post or reel from Dr Michael Okun.',
    170,
  )
  const canonicalPath = `${DR_MICHAEL_OKUN_REELS_ROUTE}/${reel.reelId}`
  const normalizedMediaUrl = reel.mediaUrl?.trim() ?? ''
  const hasMediaUrl = normalizedMediaUrl.length > 0
  const mediaTypeToken = reel.mediaType?.toUpperCase() ?? ''
  const prefersVideoPlayback =
    mediaTypeToken.includes('VIDEO') || reel.mediaKind === 'reel'
  const playbackKey = `${reel.reelId}:${normalizedMediaUrl}`
  const embedUrl = toInstagramEmbedUrl(reel.permalink)
  const shouldRenderVideo =
    prefersVideoPlayback && hasMediaUrl && failedPlaybackKey !== playbackKey
  const shouldRenderEmbed =
    reel.mediaKind === 'reel' && !shouldRenderVideo && Boolean(embedUrl)
  const shareUrl = toAbsoluteUrl(canonicalPath)
  const shareImageUrl = reel.displayImageUrl ? toAbsoluteUrl(reel.displayImageUrl) : undefined
  const socialPostingText = reel.caption?.trim() || pageDescription
  const socialPostingImage = reel.displayImageUrl ? [toAbsoluteUrl(reel.displayImageUrl)] : undefined
  const breadcrumbSchema = buildBreadcrumbList([
    { name: 'Home', pathOrUrl: '/' },
    { name: 'Prof Michael Okun: My Take', pathOrUrl: DR_MICHAEL_OKUN_REELS_ROUTE },
    { name: titleLabel, pathOrUrl: canonicalPath },
  ])
  const reelSchema = {
    '@context': 'https://schema.org',
    '@type': prefersVideoPlayback ? 'VideoObject' : 'SocialMediaPosting',
    name: titleLabel,
    headline: titleLabel,
    description: pageDescription,
    url: toAbsoluteUrl(canonicalPath),
    uploadDate: prefersVideoPlayback ? reel.timestamp?.toISOString() : undefined,
    datePublished: reel.timestamp?.toISOString(),
    text: !prefersVideoPlayback ? socialPostingText : undefined,
    image: !prefersVideoPlayback ? socialPostingImage : undefined,
    thumbnailUrl: reel.displayImageUrl ? [toAbsoluteUrl(reel.displayImageUrl)] : undefined,
    contentUrl: reel.mediaUrl ?? undefined,
    embedUrl: embedUrl ?? undefined,
    author: {
      '@type': 'Person',
      name: 'Prof Michael Okun',
      url: 'https://www.instagram.com/michaelokun/',
    },
    publisher: {
      '@type': 'Organization',
      name: "Parkinson's Insights",
      url: toAbsoluteUrl('/'),
    },
  }

  return (
    <article className="bg-background">
      <SeoHead
        title={`${titleLabel} | Prof Michael Okun: My Take`}
        description={pageDescription}
        canonicalPath={canonicalPath}
        imageUrl={reel.displayImageUrl ?? undefined}
      />
      <StructuredData data={[breadcrumbSchema, reelSchema]} />
      <div className="mx-auto w-full max-w-5xl px-4 pt-4 sm:px-6 sm:pt-6 lg:px-8">
        <div className="flex items-center justify-between gap-4">
          <button
            type="button"
            onClick={navigateBackToReelsList}
            className="inline-flex items-center gap-1 text-sm font-semibold text-primary no-underline hover:text-primary-light"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Prof Michael Okun: My Take
          </button>
          <div className="ml-auto flex flex-col items-end gap-2 sm:flex-row sm:items-center">
            <FavouriteBookmarkButton
              isFavourited={isFavourited}
              onClick={() => {
                void toggle(openLoginModal)
              }}
              disabled={favouriteLoading || favouriteToggling}
              className="h-11 shrink-0 gap-2 bg-background! px-4 text-blue! hover:bg-blue/10!"
              label={isFavourited ? 'Favorited' : 'Add To Favourites'}
              ariaLabel={isFavourited ? 'Remove Instagram content from favourites' : 'Add Instagram content to favourites'}
            />
            <button
              type="button"
              onClick={() => {
                setIsShareModalOpen(true)
              }}
              aria-label="Share Instagram content"
              className="inline-flex h-11 shrink-0 items-center justify-center gap-2 rounded-full border border-blue bg-background px-4 text-blue transition-colors hover:bg-blue/10"
            >
              <FiShare2 className="h-5 w-5" aria-hidden />
              <span className="text-sm font-semibold">Share</span>
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
        {shouldRenderVideo ? (
          <InstagramReelVideoPlayer
            mediaUrl={normalizedMediaUrl}
            posterUrl={reel.displayImageUrl}
            title={`${titleLabel} video`}
            className="mb-6"
            onPlaybackUnavailable={() => {
              setFailedPlaybackKey(playbackKey)
            }}
          />
        ) : shouldRenderEmbed ? (
          <InstagramReelEmbed
            permalink={reel.permalink}
            title={`${titleLabel} on Instagram`}
            className="mb-6"
          />
        ) : reel.displayImageUrl ? (
          <img
            src={reel.displayImageUrl}
            alt={titleLabel}
            className="mb-6 w-full rounded-xl object-cover shadow-md"
          />
        ) : (
          <p className="mb-6 rounded-xl border border-border-light bg-border-light/40 p-4 text-center text-sm font-semibold text-text-secondary">
            Instagram content preview is unavailable.
          </p>
        )}

        <p className="text-sm font-bold text-primary">{formatTimestamp(reel.timestamp)}</p>
        {favouriteError ? (
          <p className="mt-2 text-sm text-text-secondary">{favouriteError}</p>
        ) : null}

        {reel.username ? (
          <p className="mt-2 text-sm font-semibold text-text-secondary">@{reel.username}</p>
        ) : null}

        {reel.caption ? (
          <p className="mt-6 whitespace-pre-wrap leading-relaxed text-text-primary">
            {reel.caption}
          </p>
        ) : null}

        {reel.permalink ? (
          <div className="mt-8 text-center">
            <a
              href={reel.permalink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                AnalyticsFeature.trackExternalLinkOpen(
                  screenPath,
                  'instagram_reel_detail',
                  'instagram_reel',
                  reel.reelId,
                )
              }}
              className="inline-flex items-center justify-center rounded-lg bg-blue px-6 py-3 text-sm font-semibold text-white no-underline transition-colors hover:bg-blue/90"
            >
              Open on Instagram
            </a>
          </div>
        ) : null}

        <hr className="mt-10 border-border-light" />

        <CommentsSection
          targetId={reel.uid}
          commentsOn={CommentsOn.InstagramReel}
          onAuthRequired={openLoginModal}
        />
      </div>
      <NewsShareModal
        isOpen={isShareModalOpen}
        onClose={() => {
          setIsShareModalOpen(false)
        }}
        shareUrl={shareUrl}
        title={titleLabel}
        description={pageDescription}
        imageUrl={shareImageUrl}
      />
    </article>
  )
}
